import { breakpoints, Column, Columns, Heading, Stack, Text, textVariants } from '@etg/wings';
import { ConnectedComboBox, ConnectedInput } from '@eti/form';
import { css } from '@eti/styles';
import { useProperty, useTranslation, useSiteContext, useValidation } from '@eti/providers';
import { constants, string } from '@eti/utils';
import orderBy from 'lodash/orderBy';
import type { GenericField } from 'redux-form';
import { Field as ReduxFormField } from 'redux-form';
import type { ComponentProps } from 'react';
import { addressFields, TAX_DATE_OF_BIRTH, TAX_ID } from '../../constants/formFields';
import MandatoryInformationMessage from '../MandatoryInformationMessage';
import MaskedDateInput from '../MaskedDateInput';

const { MOUSE_FLOW_CLASS_NAMES } = constants;

const DateOfBirthField = ReduxFormField as new () => GenericField<
  Omit<ComponentProps<typeof MaskedDateInput>, 'input' | 'meta'>
>;

const taxId = sessionStorage.getItem(TAX_ID);

const explanationStyles = css`
  color: #737373;
`;

const { STATE } = addressFields;
const columnWidth = { [breakpoints._0]: '4/4', [breakpoints._768]: '2/4' } as const;

type State = {
  label: string;
  value: string;
};

const normalize = (selectedOption: State) => selectedOption?.value ?? '';

interface TaxFormSectionProps {
  isPanAndDoBDisabled: boolean;
}

const TaxFormSection = ({ isPanAndDoBDisabled }: TaxFormSectionProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { market } = useSiteContext();

  const validate = useValidation(TAX_DATE_OF_BIRTH);

  const sortedStates = orderBy(market.states, 'label', 'asc');
  const taxIdLabel = (!isPanAndDoBDisabled && p('Payment.TaxIdFeature.Label')) || null;
  const taxIdInputTextLabel =
    string.insertArgument(t('Payment.TaxIdField.Input.Placeholder'), taxIdLabel) ||
    `Enter your ${taxIdLabel} number`;
  const labelWithoutSlash = taxIdLabel?.replace('/', '');
  const taxIdInputTextHint = t(`Payment.TaxIdField.Input.Hint.${labelWithoutSlash}`);
  const isTaxIdRequired = p('Payment.TaxIdFeature.isRequired');
  const isStateDropdownEnabled = p('Payment.State.Enabled');
  const taxRequirementLabel = t('Payment.TaxRequirement.Header.Label');
  const taxDateOfBirthEnabled = p('Payment.DateOfBirth.Enabled');
  const isTaxIdDecoupled = p('Payment.TaxIdFeature.Decoupled.Enabled');

  const format = (option: string) =>
    sortedStates.find((state: State) => state.value === option) ?? '';

  return (
    <Stack spacing={16}>
      <Stack spacing={4}>
        {isTaxIdDecoupled ? (
          <Heading level={3}>{taxRequirementLabel}</Heading>
        ) : (
          <Text as="p" variant="16-bold">
            {taxRequirementLabel}
          </Text>
        )}
        <MandatoryInformationMessage />
        {taxDateOfBirthEnabled ? (
          <Text>{t('Payment.TaxRequirement.Reason.WithDateOfBirth')}</Text>
        ) : (
          <Text className={explanationStyles} variant={textVariants._12}>
            {t('Payment.TaxRequirement.Reason')}
          </Text>
        )}
      </Stack>
      <Columns spacing={16}>
        {taxIdLabel && !taxId && (
          <Column width={columnWidth}>
            <ConnectedInput
              autoCapitalize="off"
              autoCorrect="off"
              className={MOUSE_FLOW_CLASS_NAMES}
              data-testid="taxIdInput-input"
              description={taxIdInputTextHint}
              label={taxIdInputTextLabel}
              maxLength="32"
              name={TAX_ID}
              placeholder={taxIdLabel}
              shouldMarkInputAsRequired={isTaxIdRequired}
              spellCheck="false"
              validationRuleName={TAX_ID}
            />
          </Column>
        )}
        {taxDateOfBirthEnabled && (
          <Column width={columnWidth}>
            <DateOfBirthField
              className={MOUSE_FLOW_CLASS_NAMES}
              component={MaskedDateInput}
              data-testid="taxDateOfBirthInput-input"
              dateFormat="yyyy-mm-dd"
              label={t('Payment.DateOfBirth.Input.Label')}
              name={TAX_DATE_OF_BIRTH}
              props={{
                name: TAX_DATE_OF_BIRTH,
              }}
              required={isTaxIdRequired}
              shouldMarkInputAsRequired={isTaxIdRequired}
              validate={validate}
            />
          </Column>
        )}
        {isStateDropdownEnabled && (
          <Column width={columnWidth}>
            <ConnectedComboBox
              className={MOUSE_FLOW_CLASS_NAMES}
              data-testid="states-dropdown"
              format={format}
              label={t('Payment.State.Label')}
              name={STATE}
              normalize={normalize}
              options={sortedStates}
              shouldMarkInputAsRequired
              validationRuleName={STATE}
            />
          </Column>
        )}
      </Columns>
    </Stack>
  );
};

export default TaxFormSection;
